import { Link, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import { useAllLists, useAllProjects, useAllTasks, useResource } from '@/db/hooks'
import type { SuggestionResource, SuggestionSpec, SuggestionStatus } from '@/kinds/suggestion'
import { acknowledgeCapture, describeOp, reapplyCapture, revertCapture } from '@/lib/suggestions'
import { dropCapture } from '@/lib/captures'
import { CaptureSource } from '@/components/Captures'
import { Button, Empty, PageTitle, cx } from '@/components/ui'
import { fmtRemind } from '@/components/RemindPicker'

/**
 * The receipt for a capture the assistant filed. Top to bottom: what arrived,
 * what was done with it (one line per change), the reason, then the two things
 * you can do: put it all back, or file it again after a put-back. "Got it"
 * only clears the receipt from Today; it never touches the task.
 */
export const CaptureView = () => {
  const { id } = useParams({ strict: false }) as { id: string }
  const s = useResource<SuggestionSpec, SuggestionStatus>(id) as SuggestionResource | undefined
  const tasks = useAllTasks() ?? []
  const projects = useAllProjects() ?? []
  const lists = useAllLists() ?? []
  const [busy, setBusy] = useState<'revert' | 'reapply' | null>(null)
  const [err, setErr] = useState<string | null>(null)
  if (!s || s.spec.type !== 'capture') return <Empty icon="📥">No such capture.</Empty>
  const titleOf = (rid: string) =>
    tasks.find((t) => t.id === rid)?.spec.title ??
    projects.find((p) => p.id === rid)?.spec.title ??
    lists.find((l) => l.id === rid)?.spec.title
  const task = tasks.find((t) => t.id === s.spec.target_id)
  const applied = s.status.state === 'applied'
  const reverted = s.status.state === 'reverted'
  // anything else is a proposal that was never applied: say so rather than
  // calling it "put back", which is a different thing entirely
  const proposed = !applied && !reverted
  const seen = !!s.status.acknowledged_at
  const created = s.status.created ?? []
  const when = s.status.decided_at ? fmtRemind(s.status.decided_at) : ''
  const run = (what: 'revert' | 'reapply') => {
    setBusy(what)
    void (what === 'revert' ? revertCapture(s) : reapplyCapture(s)).finally(() => setBusy(null))
  }
  return (
    <div data-testid="capture-receipt" data-state={s.status.state}>
      <PageTitle
        eyebrow="Capture"
        right={
          <span
            className={cx(
              'rounded-full px-2.5 py-1 text-[13px] font-semibold',
              applied ? 'bg-accent-soft text-accent-text' : 'bg-raised text-muted',
            )}
            data-testid="capture-state"
          >
            {applied ? 'Filed' : reverted ? 'Put back' : 'Not done yet'}
            {when ? ` · ${when}` : ''}
          </span>
        }
      >
        {applied ? 'Filed for you' : reverted ? 'Put back as it arrived' : 'Waiting for you'}
      </PageTitle>

      <section className="rounded-2xl bg-panel/60 px-4 py-3 mb-5">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-muted mb-1">
          <span>What arrived</span>
          <CaptureSource s={s} />
        </div>
        <p className="text-base whitespace-pre-wrap" data-testid="capture-text">
          {s.spec.prompt || task?.spec.title || '…'}
        </p>
      </section>

      <section className="mb-5">
        <div className="text-[13px] font-semibold text-muted mb-1">
          {applied ? 'What I did with it' : reverted ? 'What I had done with it' : 'What I suggested'}
        </div>
        <p className="text-lg font-semibold tracking-tight mb-2" data-testid="capture-summary">
          {s.spec.title}
        </p>
        <ul
          className={cx('flex flex-col gap-1.5 text-sm', !applied && 'opacity-60')}
          data-testid="capture-ops"
        >
          {s.spec.ops.map((op, i) => (
            <li key={i} className="flex items-start gap-2 rounded-xl bg-panel/60 px-3 py-2">
              <span className={applied ? 'text-accent' : 'text-faint'}>{applied ? '•' : '–'}</span>
              <span className={cx(!applied && 'line-through decoration-faint')}>
                {describeOp(op, titleOf)}
              </span>
            </li>
          ))}
        </ul>
        {s.spec.rationale && (
          <p className="mt-2 text-sm text-muted" data-testid="capture-why">
            <span className="text-faint">Because </span>
            {s.spec.rationale}
          </p>
        )}
      </section>

      <section className="mb-5 flex flex-col gap-2" data-testid="capture-actions">
        {err && <p className="text-xs text-rose-300">{err}</p>}
        {applied ? (
          <Button onClick={() => run('revert')} disabled={busy !== null} data-testid="capture-revert">
            {busy === 'revert' ? 'Putting back…' : 'Put it back'}
          </Button>
        ) : (
          <Button onClick={() => run('reapply')} disabled={busy !== null} data-testid="capture-reapply">
            {busy === 'reapply' ? 'Filing…' : proposed ? 'Do it' : 'File it again'}
          </Button>
        )}
        <p className="text-xs text-faint">
          {applied
            ? 'Put it back restores the task exactly as it arrived and removes anything this made.'
            : proposed
              ? 'Nothing has happened yet. This does it.'
              : 'File it again makes the same changes as before.'}
        </p>
        {task && !task.deleted_at && (
          <>
            <Button
              variant="quiet"
              className="text-rose-300"
              onClick={() => void dropCapture(task.id, task.spec.title)}
              data-testid="capture-drop-all"
            >
              Delete it and all it made
            </Button>
            <p className="text-xs text-faint">One undo brings it all back.</p>
          </>
        )}
        {!seen && (
          <>
            <Button
              variant="primary"
              onClick={() => void acknowledgeCapture(s).catch((e: Error) => setErr(e.message))}
              data-testid="capture-ack"
            >
              Got it
            </Button>
            <p className="text-xs text-faint">Got it clears this from Today. It changes nothing else.</p>
          </>
        )}
      </section>

      {(task || created.length > 0) && (
        <section className="mb-5">
          <div className="text-[13px] font-semibold text-muted mb-1.5">Open</div>
          <div className="flex flex-wrap gap-2">
            {task && (
              <Link
                to="/tasks/$id"
                params={{ id: task.id }}
                className="inline-flex items-center gap-1 rounded-full bg-raised px-3 py-1.5 text-sm hover:bg-line"
                data-testid="capture-open-task"
              >
                ○ {task.spec.title}
              </Link>
            )}
            {created.map((c) => (
              <Link
                key={c.id}
                to={
                  c.kind === 'Project'
                    ? '/projects/$id'
                    : c.kind === 'Page'
                      ? '/pages/$id'
                      : c.kind === 'List'
                        ? '/lists/$id'
                        : '/tasks/$id'
                }
                params={{ id: c.id }}
                className="inline-flex items-center gap-1 rounded-full bg-raised px-3 py-1.5 text-sm hover:bg-line"
              >
                {c.kind === 'Page' ? '📄' : c.kind === 'Project' ? '▣' : c.kind === 'List' ? '☑' : '○'}{' '}
                {titleOf(c.id) ?? c.kind}
              </Link>
            ))}
          </div>
        </section>
      )}
      <p>
        <Link to="/captures" className="text-xs text-faint hover:text-fg">
          All captures
        </Link>
      </p>
    </div>
  )
}
